import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import { RobotoUserConfigKey } from "../ConfigRecords";

/**
 * Cache key for a user config value, partitioned by the signed-in user.
 *
 * The `userId` segment carries no information the request needs — the backend
 * resolves `v1/config/user/...` from the caller's token — but it keeps each
 * user's values in their own cache entry. Without it, a sign-out followed by a
 * different user signing in (which happens entirely in-SPA: the QueryClient is
 * a module singleton mounted above `AuthProvider`, and nothing clears it) would
 * serve the previous user's value to the new one for as long as the entry stays
 * fresh. Mirrors the `orgId` segment in `useOrgConfig`'s key.
 *
 * `null` stands in while nobody is signed in, when the query is disabled.
 */
export function getQueryKey(
  userId: string | undefined,
  key: RobotoUserConfigKey,
) {
  return ["userConfig", userId ?? null, key];
}

/**
 * Fetch a user config value from the backend.
 *
 * Disabled until the current user is known, so the returned `data` is only ever
 * the signed-in user's own value (`undefined` while unauthenticated).
 *
 * @template T The type of the config value to fetch
 * @param key - The config key to fetch
 * @returns The config value
 */

export function useUserConfig<T = unknown>(key: RobotoUserConfigKey) {
  const { configService } = useDomainServices();
  const { currentUser } = useAuth();
  const userId = currentUser?.user_id;

  return useQuery({
    queryKey: getQueryKey(userId, key),
    queryFn: ({ signal }) => {
      return configService.getUserConfig<T>(key, {
        abortSignal: signal,
      });
    },
    enabled: userId !== undefined,
  });
}
